import React from 'react';
import Axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import { plato_api_glossary_term_path } from '@/modules/routes';
import { queryClient } from '@/utils/ReactQuery';
import QueryKeys from '@/utils/QueryKeys';
import { TypeToConfirmModal, useModalManager } from '@/components/common/Modal';
import { sanitizeInlineHtml } from '@/utils/sanitizeInlineHtml';
import { unwrapGlossaryTermSpansFor } from '@/components/shared/Froala/plugins/GlossaryTerms/unwrapGlossaryTerms';
import I18n from 'i18n-js';

interface DeleteGlossaryTermButtonProps {
  glossaryTerm: {
    id: number;
    section_id: number;
    term: string;
    term_es: string;
  }
  lessonNumber: string | null;
  lessonTitle: string | null;
  onSuccess: () => void;
}

const DeleteGlossaryTermButton = ({
  glossaryTerm, lessonNumber, lessonTitle, onSuccess,
}: DeleteGlossaryTermButtonProps) => {
  const modalManager = useModalManager();

  const { mutate: deleteGlossaryTerm, isLoading, isError, isSuccess } = useMutation({
    mutationFn: () => Axios.delete(plato_api_glossary_term_path(glossaryTerm.id)),
    mutationKey: [QueryKeys.GlossaryTerms, QueryKeys.Mutations.Delete, glossaryTerm.id],
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error(err);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries([QueryKeys.GlossaryTerms, glossaryTerm.section_id]);
      modalManager.close();
    },
    onSuccess: () => {
      unwrapGlossaryTermSpansFor(glossaryTerm.id);

      if (onSuccess) onSuccess();
      modalManager.close();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    deleteGlossaryTerm();
  };

  const lessonLabel = lessonNumber
    ? `${I18n.t('lesson')} ${lessonNumber} - ${lessonTitle}`
    : lessonTitle;
  const term = I18n.locale === 'es' ? glossaryTerm.term_es : glossaryTerm.term;

  const confirmationPromptText = sanitizeInlineHtml(
    lessonTitle
      ? I18n.t('delete_glossary_term_warning', { lesson: lessonLabel, term })
      : I18n.t('delete_glossary_term_warning_no_lesson', { term }),
  );

  return (
    <>
      <button
        className="action tw-ml-1"
        disabled={isLoading || isSuccess}
        type="button"
        onClick={modalManager.open}
      >
        {I18n.t('delete')}
      </button>

      <TypeToConfirmModal
        close={modalManager.close}
        confirmationPrompt={<span dangerouslySetInnerHTML={{ __html: confirmationPromptText }} />}
        error={isError}
        headerText={I18n.t('delete_glossary_term')}
        isOpen={modalManager.isOpen}
        submitting={isLoading || isSuccess}
        warningMessage={I18n.t('deleted_terms_not_recovered')}
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default DeleteGlossaryTermButton;
